import { api } from '.';
import { StoryAdStatus, StoryAdType, StoryAgeGrade } from '../constants';
import {
  ChatieData,
  IAvailableCharacters,
  ICategory,
  IEFinderObject,
  IEpisode,
  IMyStory,
  IMyStoryAd,
  IMyStoryDetail,
  IMyStoryWaitfree,
  IMyStoryWaitfreeHistoies,
  IPagination,
  IPublishChallengeInfo,
  IReadEvent,
  IStory,
  IStoryAd,
  IStoryAdCreative,
  IStoryAdVideoGeneration,
  IStoryAdVideoGenerationConfig,
  IStoryAdWithContent,
  IStoryChatRoom,
  IStoryDetail,
  IStoryMap,
  IStoryPublishSummary,
} from '../model';
import { toQueryString } from '../utils/misc';
import { IChatResponse } from './../model.d';
import { CHAT_BOT_API } from './chatbot';

export const fetchStory = async (storyId: number) => {
  const { data } = await api.get<IStoryDetail>(`/stories/${storyId}/`);
  return data;
};

export const fetchMyStory = async (storyId: number) => {
  const { data } = await api.get<IMyStoryDetail>(`/my-stories/${storyId}/`);
  return data;
};

type FetchMyStoryListFilter = {
  childCategory__contains?: string;
  parentCategory__contains?: string;
  tapCount__gte?: number;
  tapCount__lte?: number;
};

export const fetchMyStories = async (
  filters: FetchMyStoryListFilter = {},
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IMyStory>>(
    `/my-stories/${toQueryString({ ...filters, page, perPage })}`
  );
  return data;
};

export const fetchMyLatestStory = async () => {
  const { data } = await api.get<IMyStory>('/my-stories/latest/');
  return data;
};

export interface IMyStoryPayload
  extends Pick<
    IMyStoryDetail,
    'title' | 'summary' | 'tags' | 'isFinished' | 'isCommentEnabled'
  > {
  coverImage?: IEFinderObject<'image'>;
  category?: ICategory;
  contestId?: number;
  ageGrade?: StoryAgeGrade;
}

export const DEFAULT_COVER_IMAGE: IEFinderObject<'image'> = {
  key: 'efacf33b-4afb-4b30-a5ec-7e65b21cd2f2',
  attachmentFormat: 'image',
  attachment: {
    contentType: 'image/jpeg',
    url: 'https://efinder-cdn.eineblu.me/chatie/chatie_story_cover_images/public-efacf33b-4afb-4b30-a5ec-7e65b21cd2f2.jpg',
    originalFilename: 'default_cover.jpg',
    width: 750,
    height: 560,
  },
  attachmentType: 'ChatieStoryCoverImage',
};

export const postMyStory = async (story: IMyStoryPayload) => {
  const {
    title,
    summary,
    tags,
    isFinished,
    isCommentEnabled,
    ageGrade,
    coverImage,
    category,
    contestId,
  } = story;

  ///V2스토리생성 원복상태
  const payload = {
    title,
    summary,
    isFinished,
    isCommentEnabled,
    ageGrade,
    coverImage: coverImage ?? DEFAULT_COVER_IMAGE,
    tags: tags.map((tag) => tag.id),
    categoryId: category?.id,
    contestId,
    // version: 2,
  };
  const { data } = await api.post<IMyStoryDetail>(`/my-stories/`, payload);
  return data;
};

export const patchMyStory = async ({
  id,
  ...story
}: { id: number } & IMyStoryPayload) => {
  const {
    title,
    summary,
    tags,
    isFinished,
    isCommentEnabled,
    coverImage,
    category,
  } = story;

  const payload = {
    title,
    summary,
    isFinished,
    isCommentEnabled,
    coverImage,
    tags: tags.map((tag) => tag.id),
    categoryId: category?.id,
  };
  const { data } = await api.patch<IMyStoryDetail>(
    `/my-stories/${id}/`,
    payload
  );
  return data;
};

export const deleteMyStory = async (storyId: number) => {
  const { data } = await api.delete(`/my-stories/${storyId}/`);
  return data;
};

type FetchStoriesByTagsOptions = {
  ordering?: 'weekly_popular' | 'newest';
  type?: 'all' | 'bloom';
};

export const fetchStoriesByTags = async (
  tagId: number,
  options: FetchStoriesByTagsOptions = {},
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IStory>>(
    `/tags/${tagId}/stories/${toQueryString({
      page,
      perPage,
      ordering: options.ordering ?? 'weekly_popular',
      type: options.type ?? 'all',
    })}`
  );
  return data;
};

export type Finished = 'all' | 'finished' | 'unfinished';
export type Ordering = 'popular' | 'newest' | 'popular_view_count';

export const fetchStoryByCategory = async (
  categoryId: number,
  childCategoryId?: number,
  isFinished: Finished = 'all',
  ordering: Ordering = 'popular',
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IStory>>(
    `/categories/${categoryId}/stories/${toQueryString({
      childCategoryId,
      isFinished,
      ordering,
      page,
      perPage,
    })}`
  );
  return data;
};

export interface IMyStoryBookingInfo {
  id: number;
  bookingEpisodeCount: number;
  latestBookingEpisode?: Pick<IEpisode, 'id' | 'title' | 'tapCount'> & {
    published: string;
  };
}

export const fetchMyStoryBookingInfo = async (storyId: number) => {
  const { data } = await api.get<IMyStoryBookingInfo>(
    `/my-stories/${storyId}/booking-info/`
  );
  return data;
};

/**
 * User Stories
 */

export const fetchUserStories = async (
  userId: number,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IStory>>(
    `/users/${userId}/stories/${toQueryString({ page, perPage })}`
  );
  return data;
};

export const fetchUsersOtherStory = async (
  userId: number,
  {
    excludeId,
  }: {
    excludeId: number;
  },
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IStory>>(
    `/users/${userId}/stories/${toQueryString({
      page,
      perPage,
      excludeId,
    })}`
  );
  return data;
};

export type ReadStorySort = 'update' | 'latest';

export const fetchReadStories = async (
  sort: ReadStorySort,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IStory>>(
    `/users/me/read-stories/${toQueryString({ page, perPage, sort })}`
  );
  return data;
};

export const fetchSearchedReadStories = async (
  payload: { q: string; sort?: ReadStorySort },
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IStory>>(
    `/users/me/read-stories/${toQueryString({
      page,
      perPage,
      sort: payload.sort,
      q: payload.q,
    })}`
  );
  return data;
};

export const fetchLikedStories = async (page = 1, perPage = 20) => {
  const { data } = await api.get<IPagination<IStory>>(
    `/users/me/watch-later-stories/${toQueryString({ page, perPage })}`
  );
  return data;
};

export const likeStory = async (storyId: number) => {
  const { data } = await api.post<IStory>('/users/me/watch-later-stories/', {
    storyId,
  });
  return data;
};

export const unlikeStory = async (storyId: number) => {
  const { data } = await api.delete(
    `/users/me/watch-later-stories/${storyId}/`
  );
  return data;
};

/**
 * Ad
 */

export const fetchStoryAd = async () => {
  const { data } = await api.get<IStoryAd>(
    `/story-ads/${toQueryString({ type: StoryAdType.list })}`
  );
  return data;
};

export const fetchStoryAdFull = async (storyId: number) => {
  const { data } = await api.get<IStoryAdWithContent>(
    `/story-ads/${toQueryString({ type: StoryAdType.full, storyId })}`
  );
  return data;
};

export const postClickStoryAdFull = async (storyAdId: number) => {
  const { data } = await api.post(`/story-ads/${storyAdId}/click/`, {
    type: StoryAdType.full,
  });
  return data;
};

export const postExposeStoryAdFull = async (storyAdId: number) => {
  const { data } = await api.post(`/story-ads/${storyAdId}/expose/`, {
    type: StoryAdType.full,
  });
  return data;
};

export type PostMyStoryAdPayload = {
  balloon: number;
  episodeId?: number;
  creativeId?: number;
  voucherId?: number;
};

type BuildPostMyStoryAdPayloadOptions = {
  balloon: number;
  episodeId: number;
  creativeId?: number;
  voucherId?: number;
};

export const buildPostMyStoryAdPayload = ({
  balloon,
  episodeId,
  creativeId,
  voucherId,
}: BuildPostMyStoryAdPayloadOptions): PostMyStoryAdPayload => ({
  balloon,
  ...(creativeId ? { creativeId } : { episodeId }),
  ...(voucherId ? { voucherId } : {}),
});

export const postMyStoryAd = async (
  storyId: number,
  payload: PostMyStoryAdPayload
) => {
  const { data } = await api.post<IMyStoryAd>(
    `/my-stories/${storyId}/ads/`,
    payload
  );
  return data;
};

export const fetchStoryAdVideoGenerationConfig = async (
  storyId: number,
  episodeId: number
) => {
  const { data } = await api.get<IStoryAdVideoGenerationConfig>(
    `/my-stories/${storyId}/episodes/${episodeId}/ad-video-generation-config/`
  );
  return data;
};

export const fetchStoryAdVideoGenerations = async (
  storyId: number,
  episodeId: number
) => {
  const { data } = await api.get<IPagination<IStoryAdVideoGeneration>>(
    `/my-stories/${storyId}/episodes/${episodeId}/ad-video-generations/`
  );
  return data;
};

export type PostStoryAdVideoGenerationPayload = {
  direction?: string;
  expectedBalloonCost?: number;
  idempotencyKey?: string;
  config?: {
    duration?: number;
    resolution?: string;
  };
};

export const postStoryAdVideoGeneration = async (
  storyId: number,
  episodeId: number,
  payload: PostStoryAdVideoGenerationPayload
) => {
  const { data } = await api.post<IStoryAdVideoGeneration>(
    `/my-stories/${storyId}/episodes/${episodeId}/ad-video-generations/`,
    payload
  );
  return data;
};

export const fetchStoryAdVideoGeneration = async (
  storyId: number,
  episodeId: number,
  generationId: number
) => {
  const { data } = await api.get<IStoryAdVideoGeneration>(
    `/my-stories/${storyId}/episodes/${episodeId}/ad-video-generations/${generationId}/`
  );
  return data;
};

export const fetchStoryAdCreatives = async (
  storyId: number,
  episodeId: number
) => {
  const { data } = await api.get<IPagination<IStoryAdCreative>>(
    `/my-stories/${storyId}/episodes/${episodeId}/ad-creatives/`
  );
  return data;
};

export type PostStoryAdCreativePayload =
  | {
      type: 'episode_player';
    }
  | {
      type: 'ai_vertical_video';
      videoGenerationId: number;
    };

export const postStoryAdCreative = async (
  storyId: number,
  episodeId: number,
  payload: PostStoryAdCreativePayload
) => {
  const { data } = await api.post<IStoryAdCreative>(
    `/my-stories/${storyId}/episodes/${episodeId}/ad-creatives/`,
    payload
  );
  return data;
};

export const fetchMyStoryAds = async (
  storyId: number,
  status: StoryAdStatus,
  page = 1
) => {
  const { data } = await api.get<IPagination<IMyStoryAd>>(
    `/my-stories/${storyId}/ads/${toQueryString({ status, page })}`
  );
  return data;
};

export const patchMyStoryAd = async (
  storyId: number,
  storyAdId: number,
  status: StoryAdStatus
) => {
  const { data } = await api.patch(`/my-stories/${storyId}/ads/${storyAdId}/`, {
    status,
  });
  return data;
};

export const postWatchLaterStory = async (storyId: number) => {
  const { data } = await api.post<IStoryDetail>(
    '/users/me/watch-later-stories/',
    { storyId }
  );
  return data;
};

export const deleteWatchLaterStory = async (storyId: number) => {
  await api.delete(`/users/me/watch-later-stories/${storyId}/`);
  return undefined;
};

export const deleteReadStory = async (storyId: number) => {
  return await api.delete(`/users/me/read-stories/${storyId}/`);
};

export const deleteReadStories = async (storyIds: number[]) =>
  api.delete('/users/me/read-stories/bulk/', { data: { storyIds: storyIds } });

/**
 * donation
 */
export type StoryDonationPayload = {
  balloon: number;
  message?: string;
  episodeId?: number;
  voucherId?: number;
};

export const postStoryDonation = async (
  storyId: number,
  payload: StoryDonationPayload
) => {
  const { data } = await api.post(`/stories/${storyId}/donation/`, payload);
  return data;
};

// waitfree
export type PostMyStoryWaitfreePayload = {
  firstWaitfreeEpisodeId?: number;
  lastWaitfreeEpisodeId?: number;
};

export const postMyStoryWaitfree = async (
  storyId: number,
  payload?: PostMyStoryWaitfreePayload
) => {
  const { data } = await api.post<IMyStoryWaitfree>(
    `/my-stories/${storyId}/toggle-waitfree/`,
    payload
  );
  return data;
};

export const fetchMyStoryWaitfree = async (storyId: number) => {
  const { data } = await api.get<IMyStoryWaitfree>(
    `/my-stories/${storyId}/waitfree/`
  );
  return data;
};

export const fetchMyStoryWaitfreeDailyHistories = async (
  storyId: number,
  year: number,
  month: number
) => {
  const { data } = await api.get<IMyStoryWaitfreeHistoies>(
    `/my-stories/${storyId}/waitfree/daily-histories/${toQueryString({
      year,
      month,
    })}`
  );
  return data;
};

export const patchMyStoryWaitfree = async (
  storyId: number,
  payload: PostMyStoryWaitfreePayload
) => {
  const { data } = await api.patch<IMyStoryWaitfree>(
    `/my-stories/${storyId}/waitfree/`,
    payload
  );
  return data;
};

export interface IStoryPurchaseInformation {
  hasPurchased: boolean;
}

export const fetchStoryPurchaseInformation = async (
  storyId: number,
  start?: string,
  end?: string
) => {
  const created__gte = start;
  const created__lte = end;

  const { data } = await api.get<IStoryPurchaseInformation>(
    `/stories/${storyId}/purchase-info/${toQueryString({
      created__gte,
      created__lte,
    })}`
  );

  return data;
};

export const fetchStoryIknow = async (storyId: number) => {
  const { data } = await api.get<IStoryMap>(`/stories/${storyId}/iknow-map/`);
  return data;
};

export const getReadEvent = async (code: string) => {
  const { data } = await api.get<IReadEvent>(`/read-events/${code}/`);
  return data;
};

export const getPublishChallengeInfo = async (storyId: number) => {
  const { data } = await api.get<IPublishChallengeInfo>(
    `/my-stories/${storyId}/pub-challenge-info/`
  );
  return data;
};

export const getMyWeeklySumamries = async (storyId: number) => {
  const { data } = await api.get<IStoryPublishSummary[]>(
    `/my-stories/${storyId}/weekly-summaries/`
  );
  return data;
};

export const getWeeklySumamries = async (storyId: number) => {
  const { data } = await api.get<IStoryPublishSummary[]>(
    `/stories/${storyId}/weekly-summaries/`
  );
  return data;
};

export type StoryChatRoomApiPayload = {
  story_id: number | string;
  episode_id: number | string;
  episode_json: ChatieData;
  profile_uuid: string;
};

export const postStoryChatRoom = async (payload?: StoryChatRoomApiPayload) => {
  const { data } = await CHAT_BOT_API.post<IChatResponse<IStoryChatRoom>>(
    `/episode-chat-rooms/`,
    payload
  );
  return data;
};

export const fetchStoryChatRoom = async (id: number | string) => {
  const { data } = await CHAT_BOT_API.get<IChatResponse<IStoryChatRoom>>(
    `/episode-chat-rooms/${id}/`
  );
  return data?.result;
};

export const createAvailableCharacter = async (json: ChatieData) => {
  const { data } = await CHAT_BOT_API.post<IChatResponse<IAvailableCharacters>>(
    `/episode-chat-rooms/create-info/`,
    {
      episode_json: json,
    }
  );
  return data?.result;
};
